// Policy & Alerts: per-camera watchbox policies.
//
// A "policy" sends a crop to the captioner with a custom yes/no prompt chain.
// Two crop sources:
//   - region:    a user-drawn box on the camera frame, cropped every
//                interval_s seconds; stored as a named manual track.
//   - detection: the normal person crops on that camera, re-evaluated against
//                the policy prompt (throttled by cooldown_s).
// A YES answer to prompt 1 raises an alert (custom text) in the top ribbon; an
// optional prompt 2 escalates it via a priority second pass, which either
// confirms/dismisses the alert (YES/NO) or attaches a descriptive answer to it.

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, ShieldCheck, SquareDashedMousePointer, Trash2 } from "lucide-react";

import { api, Camera, Policy, PolicyInput, PolicySource } from "../api";
import { useRole } from "../hooks/useRole";
import { Modal } from "../components/Modal";
import { PageHeader } from "../components/PageHeader";
import { SearchableSelect } from "../components/SearchableSelect";
import { useScope } from "../components/ScopeContext";
import { useWords } from "../words";

const DEFAULT_PROMPT1 =
  "Look only at this region of a CCTV image. Answer with one word only, YES or NO: is the condition of interest present?";
const DEFAULT_PROMPT2 =
  "Re-examine this region carefully. Reply YES only if the condition is clearly present with high certainty, otherwise reply NO. Answer with one word only: YES or NO.";
const DEFAULT_ALERT_TEXT = "Immediate action required";
const DEFAULT_PROMPT2_DESCRIBE = "Describe what you see in this region in one or two sentences.";

type DraftState = {
  id?: string;
  camera_id: string;
  /** Chosen first, to narrow the camera list. The server derives the stored
   *  store from the camera, so this is a filter rather than a second source of
   *  truth that could disagree with it. */
  store: string;
  name: string;
  enabled: boolean;
  source: PolicySource;
  bbox: number[] | null;
  interval_s: number;
  cooldown_s: number;
  prompt1: string;
  prompt2: string | null;
  prompt2_yes_no: boolean;
  alert_text: string;
};

function emptyDraft(cameraId: string, store = ""): DraftState {
  return {
    camera_id: cameraId,
    store,
    name: "",
    enabled: true,
    source: "region",
    bbox: null,
    interval_s: 10,
    cooldown_s: 30,
    prompt1: DEFAULT_PROMPT1,
    prompt2: null,
    prompt2_yes_no: true,
    alert_text: DEFAULT_ALERT_TEXT,
  };
}

function draftFromPolicy(p: Policy): DraftState {
  return {
    id: p.id,
    camera_id: p.camera_id,
    store: p.store ?? "",
    name: p.name,
    enabled: p.enabled,
    source: p.source,
    bbox: p.bbox ?? null,
    interval_s: p.interval_s,
    cooldown_s: p.cooldown_s,
    prompt1: p.prompt1 || DEFAULT_PROMPT1,
    prompt2: p.prompt2 ?? null,
    prompt2_yes_no: p.prompt2_yes_no ?? true,
    alert_text: p.alert_text || DEFAULT_ALERT_TEXT,
  };
}

// ---- Region box drawing --------------------------------------------------

function RegionBoxEditor({
  cameraId,
  bbox,
  onChange,
}: {
  cameraId: string;
  bbox: number[] | null;
  onChange: (bbox: number[]) => void;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  const [draftBox, setDraftBox] = useState<number[] | null>(bbox);

  useEffect(() => setDraftBox(bbox), [bbox]);

  function loadFrame() {
    if (!cameraId) return;
    setLoading(true);
    setError(null);
    fetch(api.policies.snapshotUrl(cameraId), { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.detail || `snapshot ${r.status}`);
        }
        return r.blob();
      })
      .then((blob) => setSrc(URL.createObjectURL(blob)))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  function toNorm(e: React.PointerEvent): { x: number; y: number } {
    const rect = boxRef.current!.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    return { x, y };
  }

  function onPointerDown(e: React.PointerEvent) {
    if (!src) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = toNorm(e);
    setDraftBox(null);
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!dragRef.current) return;
    const p = toNorm(e);
    const s = dragRef.current;
    setDraftBox([
      Math.min(s.x, p.x),
      Math.min(s.y, p.y),
      Math.max(s.x, p.x),
      Math.max(s.y, p.y),
    ]);
  }

  function onPointerUp() {
    if (!dragRef.current || !draftBox) {
      dragRef.current = null;
      return;
    }
    dragRef.current = null;
    const [x1, y1, x2, y2] = draftBox;
    if (x2 - x1 > 0.01 && y2 - y1 > 0.01) onChange(draftBox);
  }

  const boxStyle = draftBox
    ? {
        left: `${draftBox[0] * 100}%`,
        top: `${draftBox[1] * 100}%`,
        width: `${(draftBox[2] - draftBox[0]) * 100}%`,
        height: `${(draftBox[3] - draftBox[1]) * 100}%`,
      }
    : null;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-bf-text">Region box</span>
        <button
          type="button"
          className="bf-button bg-white/10 bg-none text-xs"
          onClick={loadFrame}
          disabled={!cameraId || loading}
        >
          {loading ? "Loading frame…" : src ? "Reload frame" : "Load live frame"}
        </button>
      </div>
      <div
        ref={boxRef}
        className="relative aspect-video w-full select-none overflow-hidden rounded-lg border border-white/10 bg-black/60"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{ cursor: src ? "crosshair" : "default", touchAction: "none" }}
      >
        {src ? (
          <img
            src={src}
            alt="Camera frame"
            draggable={false}
            className="pointer-events-none absolute inset-0 h-full w-full object-contain"
          />
        ) : (
          <div className="grid h-full place-items-center px-4 text-center text-xs text-bf-muted">
            {error
              ? error
              : "Load a live frame, then click and drag to draw the region box."}
          </div>
        )}
        {boxStyle && (
          <div
            className="pointer-events-none absolute border-2 border-bf-magenta bg-bf-magenta/20"
            style={boxStyle}
          />
        )}
      </div>
      {draftBox && (
        <div className="mt-1 text-[11px] text-bf-muted">
          Box: [{draftBox.map((v) => v.toFixed(3)).join(", ")}] (normalized)
        </div>
      )}
    </div>
  );
}

// ---- Editor modal --------------------------------------------------------

function PolicyEditor({
  draft: initial,
  cameras,
  onClose,
}: {
  draft: DraftState;
  cameras: Camera[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { stores } = useScope();
  const w = useWords();
  const [draft, setDraft] = useState<DraftState>(initial);
  const [useEscalation, setUseEscalation] = useState<boolean>(
    !!initial.prompt2 && initial.prompt2.trim().length > 0,
  );
  const isEdit = !!initial.id;

  const cameraChoices = useMemo(
    () =>
      draft.store
        ? cameras.filter((c) => (c.store ?? "") === draft.store)
        : cameras,
    [cameras, draft.store],
  );

  const set = <K extends keyof DraftState>(k: K, v: DraftState[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const save = useMutation({
    mutationFn: async () => {
      const body: PolicyInput = {
        camera_id: draft.camera_id,
        store: draft.store,
        name: draft.name,
        enabled: draft.enabled,
        source: draft.source,
        bbox: draft.source === "region" ? draft.bbox : null,
        interval_s: draft.interval_s,
        cooldown_s: draft.cooldown_s,
        prompt1: draft.prompt1,
        prompt2: useEscalation
          ? draft.prompt2 || (draft.prompt2_yes_no ? DEFAULT_PROMPT2 : DEFAULT_PROMPT2_DESCRIBE)
          : null,
        prompt2_yes_no: draft.prompt2_yes_no,
        alert_text: draft.alert_text,
      };
      return isEdit
        ? api.policies.update(initial.id!, body)
        : api.policies.create(body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["policies"] });
      onClose();
    },
  });

  const canSave =
    !!draft.camera_id &&
    draft.prompt1.trim().length > 0 &&
    (draft.source !== "region" ||
      (!!draft.bbox &&
        draft.bbox.length === 4 &&
        Number.isFinite(draft.interval_s) &&
        draft.interval_s >= 0.1));

  return (
    <Modal onClose={onClose} labelledBy="policy-editor-title" panelClassName="w-full max-w-2xl">
      <h2 id="policy-editor-title" className="mb-3 text-lg font-semibold text-bf-text">
        {isEdit ? "Edit policy" : "New policy"}
      </h2>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="block">
          <span className="mb-1 block text-sm text-bf-muted">{w.Store}</span>
          <SearchableSelect
            ariaLabel={w.Store}
            value={draft.store}
            onChange={(store) => {
              set("store", store);
              // A camera from another store would contradict the choice just
              // made, so clear it unless it belongs here.
              const cam = cameras.find((c) => c.id === draft.camera_id);
              if (store && cam && (cam.store ?? "") !== store) set("camera_id", "");
            }}
            placeholder={w.allStores}
            options={[
              { value: "", label: w.allStores },
              ...stores.map((s) => ({
                value: s.name,
                label: s.display_name || s.name,
                hint: s.location || undefined,
                badge: s.is_simulation ? "Sim" : undefined,
                keywords: s.name,
              })),
            ]}
          />
        </div>
        <div className="block">
          <span className="mb-1 block text-sm text-bf-muted">Camera</span>
          <SearchableSelect
            ariaLabel="Camera"
            value={draft.camera_id}
            onChange={(id) => {
              set("camera_id", id);
              // Adopt the camera's store, so the policy is always filed where
              // the camera actually lives.
              const cam = cameras.find((c) => c.id === id);
              if (cam?.store) set("store", cam.store);
            }}
            placeholder="Select a camera…"
            options={cameraChoices.map((c) => ({
              value: c.id,
              label: c.name || c.id,
              hint: c.store || undefined,
              keywords: c.id,
              badge: c.role === "view_only" ? "Non-AI" : undefined,
            }))}
          />
          {cameraChoices.length === 0 && (
            <span className="mt-1 block text-[11px] text-amber-300">
              No cameras in this store.
            </span>
          )}
        </div>

        <label className="block">
          <span className="mb-1 block text-sm text-bf-muted">Name</span>
          <input
            className="bf-input w-full"
            value={draft.name}
            placeholder="e.g. Front door left open"
            onChange={(e) => set("name", e.target.value)}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm text-bf-muted">Source</span>
          <select
            className="bf-input w-full"
            value={draft.source}
            onChange={(e) => set("source", e.target.value as PolicySource)}
          >
            <option value="region">Region (user-drawn box, interval)</option>
            <option value="detection">Detection (person crops)</option>
          </select>
        </label>

        {draft.source === "region" ? (
          <label className="block">
            <span className="mb-1 block text-sm text-bf-muted">Interval (seconds)</span>
            <input
              type="number"
              min={0.1}
              step={0.1}
              className="bf-input w-full"
              value={draft.interval_s}
              onChange={(e) => set("interval_s", Number(e.target.value))}
            />
            <span className="mt-1 block text-[11px] text-bf-muted">
              Down to 0.1s. Each tick is a vLLM call.
            </span>
          </label>
        ) : (
          <label className="block">
            <span className="mb-1 block text-sm text-bf-muted">Cooldown (seconds)</span>
            <input
              type="number"
              min={0}
              className="bf-input w-full"
              value={draft.cooldown_s}
              onChange={(e) => set("cooldown_s", Number(e.target.value))}
            />
          </label>
        )}
      </div>

      {draft.source === "region" && (
        <div className="mt-4">
          <RegionBoxEditor
            cameraId={draft.camera_id}
            bbox={draft.bbox}
            onChange={(b) => set("bbox", b)}
          />
        </div>
      )}

      <label className="mt-4 block">
        <span className="mb-1 block text-sm text-bf-muted">Prompt 1 (yes/no)</span>
        <textarea
          className="bf-input min-h-[14rem] w-full resize-y"
          value={draft.prompt1}
          onChange={(e) => set("prompt1", e.target.value)}
        />
      </label>

      <label className="mt-3 flex items-center gap-2 text-sm text-bf-text">
        <input
          type="checkbox"
          checked={useEscalation}
          onChange={(e) => setUseEscalation(e.target.checked)}
        />
        Escalate with a second prompt (verification pass)
      </label>
      {useEscalation && (
        <div className="mt-2">
          <span className="mb-1 block text-sm text-bf-muted">Second prompt answer</span>
          <div className="mb-2 flex flex-col gap-1">
            <label className="flex items-start gap-2 text-sm text-bf-text">
              <input
                type="radio"
                className="mt-1"
                name="prompt2-mode"
                checked={draft.prompt2_yes_no}
                onChange={() => {
                  set("prompt2_yes_no", true);
                  if (!draft.prompt2?.trim()) set("prompt2", DEFAULT_PROMPT2);
                }}
              />
              <span>
                YES/NO question
                <span className="block text-xs text-bf-muted">
                  Confirms the alert on YES, dismisses it on anything else.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm text-bf-text">
              <input
                type="radio"
                className="mt-1"
                name="prompt2-mode"
                checked={!draft.prompt2_yes_no}
                onChange={() => {
                  set("prompt2_yes_no", false);
                  if (!draft.prompt2?.trim()) set("prompt2", DEFAULT_PROMPT2_DESCRIBE);
                }}
              />
              <span>
                Descriptive answer
                <span className="block text-xs text-bf-muted">
                  Alert stays raised; the answer is attached as detail.
                </span>
              </span>
            </label>
          </div>
          <label className="block">
            <span className="mb-1 block text-sm text-bf-muted">Prompt 2 (escalation)</span>
            <textarea
              className="bf-input min-h-[14rem] w-full resize-y"
              value={
                draft.prompt2 ?? (draft.prompt2_yes_no ? DEFAULT_PROMPT2 : DEFAULT_PROMPT2_DESCRIBE)
              }
              onChange={(e) => set("prompt2", e.target.value)}
            />
          </label>
        </div>
      )}

      <label className="mt-3 block">
        <span className="mb-1 block text-sm text-bf-muted">Message</span>
        <input
          className="bf-input w-full"
          value={draft.alert_text}
          placeholder="Immediate action required"
          onChange={(e) => set("alert_text", e.target.value)}
        />
        <span className="mt-1 block text-[11px] text-bf-muted">
          Shown on the alert under the store and camera.
        </span>
      </label>

      <label className="mt-3 flex items-center gap-2 text-sm text-bf-text">
        <input
          type="checkbox"
          checked={draft.enabled}
          onChange={(e) => set("enabled", e.target.checked)}
        />
        Enabled
      </label>

      {save.isError && (
        <div className="mt-3 rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-300">
          {(save.error as Error).message}
        </div>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <button type="button" className="bf-button bg-white/10 bg-none" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="bf-button"
          disabled={!canSave || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Saving…" : isEdit ? "Save changes" : "Create policy"}
        </button>
      </div>
    </Modal>
  );
}

// ---- Page ----------------------------------------------------------------

export default function PolicyAlerts({ embedded = false }: { embedded?: boolean }) {
  const role = useRole();
  const qc = useQueryClient();
  const { scope, scopeParams, scopeKey } = useScope();
  const [editing, setEditing] = useState<DraftState | null>(null);

  const policies = useQuery({
    queryKey: ["policies", scopeKey],
    queryFn: () => api.policies.list(scopeParams),
  });
  // Every camera, so the editor can move a policy to another store without
  // first changing what the page is scoped to.
  const cameras = useQuery({
    queryKey: ["cameras", "all"],
    queryFn: () => api.cameras.list(),
  });

  const cameraNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of cameras.data ?? []) map.set(c.id, c.name || c.id);
    return map;
  }, [cameras.data]);

  const del = useMutation({
    mutationFn: (id: string) => api.policies.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["policies"] }),
  });

  const toggle = useMutation({
    mutationFn: (p: Policy) =>
      api.policies.update(p.id, {
        camera_id: p.camera_id,
        name: p.name,
        enabled: !p.enabled,
        source: p.source,
        bbox: p.bbox ?? null,
        interval_s: p.interval_s,
        cooldown_s: p.cooldown_s,
        prompt1: p.prompt1,
        prompt2: p.prompt2 ?? null,
        prompt2_yes_no: p.prompt2_yes_no ?? true,
        alert_text: p.alert_text,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["policies"] }),
  });

  if (!role.isLoading && !role.canManage) {
    return (
      <div className="bf-card p-6 text-bf-muted">
        Policies are available to administrators.
      </div>
    );
  }

  const list = policies.data ?? [];
  // A new policy starts in the store the operator is already looking at.
  const defaultStore = scope.kind === "store" ? scope.store : "";
  const firstCamera =
    (cameras.data ?? []).find(
      (c) => !defaultStore || (c.store ?? "") === defaultStore,
    )?.id ?? "";

  const newPolicyButton = (
    <button
      type="button"
      className="bf-button flex items-center gap-2"
      onClick={() => setEditing(emptyDraft(firstCamera, defaultStore))}
    >
      <Plus size={16} /> New policy
    </button>
  );

  return (
    <div className={embedded ? "" : "mx-auto max-w-5xl"}>
      {embedded ? (
        <div className="mb-5 flex justify-end">{newPolicyButton}</div>
      ) : (
        <div className="mb-5">
          <PageHeader
            title="Policies"
            icon={ShieldCheck}
            description="Set rules that watch a camera and alert you when something happens."
            actions={newPolicyButton}
          />
        </div>
      )}

      {policies.isLoading ? (
        <div className="bf-card p-6 text-bf-muted">Loading policies…</div>
      ) : list.length === 0 ? (
        <div className="bf-card flex flex-col items-center gap-3 p-10 text-center">
          <SquareDashedMousePointer size={32} className="text-bf-muted" />
          <div className="text-bf-text">No policies yet</div>
          <p className="max-w-md text-sm text-bf-muted">
            Create a policy to watch a region of a camera on an interval, or to
            re-evaluate person detections with a custom prompt.
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {list.map((p) => (
            <div key={p.id} className="bf-card flex items-start justify-between gap-4 p-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-bf-text">
                    {p.name || "(unnamed policy)"}
                  </span>
                  <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-wide text-bf-muted ring-1 ring-inset ring-white/10">
                    {p.source}
                  </span>
                  {!p.enabled && (
                    <span className="rounded-full bg-amber-400/10 px-2 py-0.5 text-[10px] uppercase tracking-wide text-amber-300 ring-1 ring-inset ring-amber-400/30">
                      disabled
                    </span>
                  )}
                </div>
                <div className="mt-1 text-sm text-bf-muted">
                  {p.store && <span className="text-bf-text">{p.store}</span>}
                  {p.store && " · "}
                  {cameraNameById.get(p.camera_id) ?? p.camera_id} ·{" "}
                  {p.source === "region"
                    ? `every ${p.interval_s}s`
                    : `cooldown ${p.cooldown_s}s`}
                  {p.prompt2 ? ((p.prompt2_yes_no ?? true) ? " · verifies" : " · describes") : ""}
                </div>
                <div className="mt-1 line-clamp-1 text-xs text-bf-muted/80">
                  Message: {p.alert_text}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  className="bf-button bg-white/10 bg-none text-xs"
                  onClick={() => toggle.mutate(p)}
                >
                  {p.enabled ? "Disable" : "Enable"}
                </button>
                <button
                  type="button"
                  className="bf-button bg-white/10 bg-none text-xs"
                  onClick={() => setEditing(draftFromPolicy(p))}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="rounded-md p-2 text-bf-muted transition hover:bg-white/10 hover:text-red-300"
                  aria-label="Delete policy"
                  onClick={() => {
                    if (confirm(`Delete policy "${p.name || p.id}"?`)) del.mutate(p.id);
                  }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <PolicyEditor
          draft={editing}
          cameras={cameras.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
